import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta.tsx';
import EmailLink from '@/components/ui/EmailLink.tsx';
import { ROUTES } from '@/config/routes';
import { BabyIcon, Bot, Download, Globe2, Lock, Mail, Shield, Trash2, UserCheck } from 'lucide-react';

// ─── Données collectées ───────────────────────────────────────────────────────
// Colonne "obligatoire" ajoutée pour satisfaire Art. 13.2.e RGPD.
const donneesCollectees = [
  {
    donnee: 'Nom et prénom',
    finalite: 'Identification, profil, résultats sportifs publics',
    baseLegale: 'Exécution du contrat',
    obligatoire: 'Oui (inscription impossible sans)',
    duree: 'Durée du compte + 3 ans (prescription civile)',
  },
  {
    donnee: 'Adresse email',
    finalite: 'Connexion, notifications, communications de service',
    baseLegale: 'Exécution du contrat',
    obligatoire: 'Oui',
    duree: 'Durée du compte + 3 ans',
  },
  {
    donnee: 'Numéro de téléphone',
    finalite: "Contact capitaine d'équipe, alertes de dernière minute",
    baseLegale: 'Intérêt légitime (contact opérationnel le jour du concours)',
    obligatoire: 'Non (facultatif)',
    duree: 'Durée du compte',
  },
  {
    donnee: 'Date de naissance',
    finalite: "Catégories d'âge FFPJP (Minimes, Cadets, Juniors, Seniors, Vétérans)",
    baseLegale: 'Exécution du contrat + obligation sportive',
    obligatoire: 'Oui',
    duree: 'Durée du compte + 5 ans',
  },
  {
    donnee: 'Numéro de licence fédérale (FFPJP…)',
    finalite:
      "Renseigné par l'utilisateur pour l'inscription, l'organisation et les exports. Non vérifié automatiquement auprès d'un référentiel fédéral dans cette version.",
    baseLegale: 'Exécution du contrat',
    obligatoire: 'Oui pour les concours réservés aux licenciés',
    duree: 'Durée du compte + 5 ans',
  },
  {
    donnee: 'Résultats et classements sportifs',
    finalite: 'Historique du joueur, podiums publics, archives sportives',
    baseLegale: 'Intérêt légitime (publication des résultats de compétitions sportives publiques)',
    obligatoire: 'Généré par votre participation',
    duree: 'Archivage indéfini (résultats publics), dissociés du compte en cas de suppression',
  },
  {
    donnee: 'Données de localisation (ville, département, GPS navigateur)',
    finalite: 'Recherche de concours par proximité',
    baseLegale: 'Consentement (géoloc précise navigateur) / Exécution du contrat (ville déclarée)',
    obligatoire: 'Non pour la géoloc précise',
    duree: 'Session / Durée du compte',
  },
  {
    donnee: 'Données de paiement (Stripe)',
    finalite: "Traitement des paiements d'inscription",
    baseLegale: 'Exécution du contrat',
    obligatoire: 'Oui pour les inscriptions payantes',
    duree:
      'Données CB : jamais stockées chez nous. Transactions (montant, date, parties) : 10 ans (obligation comptable, art. L.123-22 C. com.)',
  },
  {
    donnee: 'Données KYC organisateurs (Stripe Connect)',
    finalite: "Vérification d'identité et d'IBAN pour les virements",
    baseLegale: 'Obligation légale (LCB-FT, art. L.561-5 et s. CMF)',
    obligatoire: 'Oui pour devenir organisateur payant',
    duree: '5 ans après la fin de la relation (art. L.561-12 CMF) — responsable : Stripe',
  },
  {
    donnee: 'Données de connexion (IP, user-agent, horodatages)',
    finalite: 'Sécurité, détection de fraude, preuves en cas de litige',
    baseLegale: 'Intérêt légitime (test de balance effectué) + Obligation légale (LCEN)',
    obligatoire: 'Collectées automatiquement',
    duree: '12 mois (décret n° 2021-1362)',
  },
  {
    donnee: 'Cookies techniques (session, refresh token, CSRF)',
    finalite: 'Maintien de session authentifiée, protection CSRF',
    baseLegale: 'Exempt de consentement (strictement nécessaire — art. 82 LIL)',
    obligatoire: 'Oui pour utiliser les espaces connectés',
    duree: '7 jours (refresh), session (autres)',
  },
  {
    donnee: 'Cookies analytiques (si activés)',
    finalite: "Mesure d'audience anonymisée",
    baseLegale: 'Consentement (art. 82 LIL)',
    obligatoire: 'Non',
    duree: '13 mois maximum (reco CNIL)',
  },
];

// ─── Droits utilisateurs ──────────────────────────────────────────────────────
const droitsUtilisateurs = [
  {
    droit: "Droit d'accès (Art. 15)",
    desc: 'Obtenir confirmation du traitement de vos données et en recevoir une copie.',
    icon: UserCheck,
  },
  {
    droit: 'Droit de rectification (Art. 16)',
    desc: 'Corriger des données inexactes ou compléter des données incomplètes. La plupart des corrections sont directement disponibles dans les paramètres de votre compte.',
    icon: UserCheck,
  },
  {
    droit: "Droit à l'effacement (Art. 17)",
    desc: 'Demander la suppression de votre compte. Les résultats sportifs publiés dans le cadre de compétitions officielles restent archivés (archives sportives publiques — art. 17.3.b RGPD), mais ils sont dissociés de votre espace personnel. Réponse sous 30 jours.',
    icon: Trash2,
  },
  {
    droit: 'Droit à la portabilité (Art. 20)',
    desc: 'Recevoir vos données dans un format structuré (JSON) et les transférer à un autre service. Export envoyé sous 30 jours.',
    icon: Download,
  },
  {
    droit: "Droit d'opposition (Art. 21)",
    desc: "Vous opposer au traitement fondé sur l'intérêt légitime, notamment la prospection commerciale (lien de désinscription dans chaque email marketing).",
    icon: UserCheck,
  },
  {
    droit: 'Droit à la limitation (Art. 18)',
    desc: 'Demander la suspension temporaire du traitement de vos données dans les cas prévus par le RGPD.',
    icon: UserCheck,
  },
  {
    droit: 'Droit de retirer votre consentement (Art. 7.3)',
    desc: 'Pour les traitements fondés sur votre consentement (cookies analytiques, géolocalisation précise), vous pouvez retirer votre consentement à tout moment, sans que cela ne compromette la licéité des traitements antérieurs.',
    icon: UserCheck,
  },
  {
    droit: 'Droit de définir des directives post-mortem',
    desc: "Conformément à la loi Informatique et Libertés (art. 85), vous pouvez définir des directives relatives au sort de vos données après votre décès, générales (auprès d'un tiers de confiance certifié CNIL) ou particulières (auprès de nous).",
    icon: UserCheck,
  },
];

// ─── Sous-traitants / responsables de traitement tiers ────────────────────────
const sousTraitants = [
  {
    nom: 'Stripe Payments Europe, Ltd. / Stripe, Inc.',
    role: 'Paiements & Stripe Connect',
    lieu: 'Dublin (Irlande) + San Francisco (USA)',
    garantie: 'Clauses contractuelles types UE + Stripe certifié EU-US Data Privacy Framework',
    detail:
      "Traitement des paiements d'inscription et virements vers les organisateurs (Stripe Connect Standard). " +
      'Pour les données KYC des organisateurs, Stripe agit en qualité de **responsable de traitement indépendant** ' +
      "(relation contractuelle directe entre Stripe et l'organisateur). Pour les données de paiement des joueurs, " +
      'Stripe agit en qualité de sous-traitant de BoulesConnect et de responsable de traitement indépendant ' +
      'pour ses propres obligations réglementaires.',
  },
  {
    nom: 'OVH Cloud',
    role: 'Hébergement applicatif et base de données',
    lieu: 'France (Roubaix / Gravelines)',
    garantie: 'Hébergeur UE — certifié ISO 27001 et ISO 27701',
    detail:
      "Hébergement des serveurs applicatifs, de la base de données PostgreSQL et des sauvegardes. Aucune donnée n'est stockée hors de l'Union européenne chez ce sous-traitant.",
  },
  {
    nom: 'Mailjet (Sinch France SAS)',
    role: 'Emails transactionnels',
    lieu: 'Paris, France',
    garantie: 'Hébergement UE — certifié ISO 27001',
    detail: 'Envoi des emails de vérification, réinitialisation de mot de passe et notifications de concours.',
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PolitiqueRgpd() {
  return (
    <>
      <PageMeta
        title='Politique de confidentialité & RGPD'
        description='Politique de protection des données personnelles de BoulesConnect. Responsable de traitement, données collectées, droits des utilisateurs, mineurs, décisions automatisées.'
      />

      {/* Hero */}
      <section className='px-6 pb-16 pt-40'>
        <div className='mx-auto max-w-4xl text-center'>
          <div className='mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2'>
            <Shield className='h-4 w-4 text-blue-600' />
            <span className='text-xs font-bold uppercase tracking-wider text-blue-700'>Vie privée</span>
          </div>
          <h1 className='mb-4 text-3xl font-extrabold text-slate-900 md:text-5xl'>
            Politique de Confidentialité & RGPD
          </h1>
          <p className='text-lg text-slate-500'>Dernière mise à jour : 20 avril 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className='px-6 pb-20'>
        <div className='mx-auto max-w-4xl space-y-8'>
          {/* Responsable du traitement */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>1. Responsable du traitement</h2>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                Le responsable du traitement des données personnelles collectées via la plateforme BoulesConnect est :
              </p>
              <div className='rounded-2xl bg-slate-50 p-4'>
                <p>
                  <strong className='text-slate-900'>MADSAAS SAS</strong>
                </p>
                <p>05110 Lardier-et-Valença, France</p>
                <p>SIREN : 821 350 600 — SIRET : 821 350 600 00036 — TVA : FR00821350600</p>
                <p>Représentée par M. Marc-Antoine DUTOIT, Président</p>
                <p>
                  Contact protection des données :{' '}
                  <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />
                </p>
              </div>
            </div>
          </div>

          {/* Données collectées par rôle */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>2. Données collectées selon votre rôle</h2>
            <div className='mb-4 grid gap-4 sm:grid-cols-3 text-sm'>
              <div className='rounded-2xl bg-blue-50 p-4'>
                <p className='mb-1 font-bold text-blue-900'>Joueur / Membre</p>
                <p className='text-xs text-blue-700'>
                  Nom, email, date de naissance, n° de licence, équipes & résultats sportifs, ville
                </p>
              </div>
              <div className='rounded-2xl bg-violet-50 p-4'>
                <p className='mb-1 font-bold text-violet-900'>Organisateur</p>
                <p className='text-xs text-violet-700'>
                  Profil organisation, données Stripe Connect (KYC : identité, IBAN), données de facturation
                </p>
              </div>
              <div className='rounded-2xl bg-emerald-50 p-4'>
                <p className='mb-1 font-bold text-emerald-900'>Responsable de club</p>
                <p className='text-xs text-emerald-700'>Profil club, liste des membres, demandes d'adhésion</p>
              </div>
            </div>
            <p className='text-xs text-slate-500'>
              Nous appliquons le principe de minimisation (Art. 5.1.c RGPD) : seules les données strictement nécessaires
              à chaque rôle sont collectées.
            </p>
          </div>

          {/* Tableau détaillé */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>3. Tableau détaillé des traitements</h2>
            <p className='mb-4 text-sm text-slate-600'>
              Pour chaque donnée : finalité, base légale, caractère obligatoire ou facultatif, et durée de conservation.
            </p>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-slate-50'>
                    <th className='rounded-tl-xl px-4 py-3 text-left font-bold text-slate-900'>Donnée</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900'>Finalité</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900'>Base légale</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900'>Caractère</th>
                    <th className='rounded-tr-xl px-4 py-3 text-left font-bold text-slate-900'>Durée</th>
                  </tr>
                </thead>
                <tbody>
                  {donneesCollectees.map((row, i) => (
                    <tr key={row.donnee} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className='px-4 py-3 font-medium text-slate-900'>{row.donnee}</td>
                      <td className='px-4 py-3 text-slate-600'>{row.finalite}</td>
                      <td className='px-4 py-3'>
                        <span className='rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700'>
                          {row.baseLegale}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-slate-600 text-xs'>{row.obligatoire}</td>
                      <td className='px-4 py-3 text-slate-500 text-xs'>{row.duree}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className='mt-4 text-xs text-slate-500'>
              Les traitements fondés sur l'intérêt légitime font l'objet d'un test de balance interne documenté («
              Legitimate Interest Assessment ») vérifiant que vos droits et libertés ne sont pas disproportionnellement
              impactés. Vous pouvez vous y opposer à tout moment (voir section « Vos droits »).
            </p>
          </div>

          {/* Données collectées indirectement — NOUVEAU */}
          <div className='rounded-3xl border border-amber-100 bg-amber-50/40 p-8 shadow-sm'>
            <div className='mb-4 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100'>
                <Globe2 className='h-5 w-5 text-amber-700' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>
                4. Données collectées indirectement (Art. 14 RGPD)
              </h2>
            </div>
            <div className='space-y-3 text-sm leading-relaxed text-slate-700'>
              <p>
                Dans le cadre de la constitution de son annuaire des clubs de petanque, de jeu provencal et de boule
                lyonnaise, BoulesConnect collecte des informations <strong>publiquement disponibles</strong> a partir
                des annuaires officiels des federations (FFPJP, FFBL). Les donnees concernees sont strictement des
                donnees d'identification d'organisations : nom du club, numero d'affiliation, adresse, coordonnees de
                contact et, le cas echeant, nom et coordonnees professionnelles du president ou du secretaire lorsqu'ils
                ont ete rendus publics par le club sur les sites des federations.
              </p>
              <p>
                <strong>Source :</strong> pages publiques des sites ffpjp.org et ffbl.fr.
              </p>
              <p>
                <strong>Finalite :</strong> permettre aux joueurs de rechercher un club par zone geographique et
                discipline, aux organisateurs de contacter les clubs pour leurs evenements, et aux clubs de revendiquer
                et enrichir leur fiche sur BoulesConnect.
              </p>
              <p>
                <strong>Base legale :</strong> interet legitime (Art. 6.1.f RGPD). Une analyse d'interet legitime (LIA)
                a ete documentee et est disponible sur demande a{' '}
                <EmailLink user='dpo' domain='boulesconnect.fr' className='font-semibold underline' />.
              </p>
              <p>
                <strong>Conservation :</strong> tant que le club figure dans l'annuaire officiel de sa federation. En
                cas de depart de la federation, de demande d'opposition ou d'effacement, les donnees sont supprimees
                sous 30 jours (Art. 12.3 RGPD).
              </p>
              <p>
                <strong>Vos droits :</strong> conformement aux articles 15 a 22 du RGPD, vous disposez d'un droit
                d'acces, de rectification, d'effacement, de limitation, d'<strong>opposition</strong> (Art. 21 — droit
                renforce car base legale = interet legitime) et de portabilite. Si vous etes responsable d'un club et
                souhaitez <strong>retirer, modifier ou revendiquer</strong> la fiche correspondante, utilisez le bouton
                « Retirer ma fiche » present sur chaque page de club ou ecrivez a{' '}
                <EmailLink user='dpo' domain='boulesconnect.fr' className='font-semibold underline' />.
              </p>
            </div>
          </div>

          {/* Mineurs — NOUVEAU */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <div className='mb-4 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-pink-50'>
                <BabyIcon className='h-5 w-5 text-pink-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>5. Mineurs (Art. 8 RGPD, Art. 45 LIL)</h2>
            </div>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                Conformément à l'article 8 du RGPD et à l'article 45 de la loi Informatique et Libertés,{' '}
                <strong>l'inscription sur BoulesConnect est réservée aux personnes âgées d'au moins 15 ans</strong>.
                Aucun compte ne peut être créé par ou pour un mineur de moins de 15 ans.
              </p>
              <p>
                Les sports de boules comportent des catégories jeunes (Minimes, Cadets, Juniors). Les joueurs de 15 à 18
                ans peuvent créer un compte et s'inscrire aux concours de manière autonome. Les titulaires de l'autorité
                parentale peuvent à tout moment demander l'accès, la rectification ou la suppression des données de leur
                enfant en contactant{' '}
                <EmailLink user='dpo' domain='boulesconnect.fr' className='font-semibold underline' />.
              </p>
            </div>
          </div>

          {/* Décisions automatisées — NOUVEAU */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <div className='mb-4 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100'>
                <Bot className='h-5 w-5 text-slate-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>
                6. Décisions automatisées et profilage (Art. 22)
              </h2>
            </div>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                BoulesConnect{' '}
                <strong>
                  ne prend aucune décision produisant des effets juridiques ou affectant significativement les
                  utilisateurs, fondée exclusivement sur un traitement automatisé
                </strong>
                , au sens de l'article 22 du RGPD.
              </p>
              <p>
                Les traitements automatisés mis en œuvre (tri géographique des concours, suggestions de concours,
                détection de paiements frauduleux côté Stripe) sont de simples aides à la navigation ou à la sécurité,
                et n'emportent aucune conséquence juridique automatique.
              </p>
            </div>
          </div>

          {/* Sous-traitants */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>7. Sous-traitants et destinataires</h2>
            <p className='mb-4 text-sm text-slate-600'>
              Vos données sont partagées uniquement avec les sous-traitants et responsables de traitement tiers
              suivants, dans la stricte limite de leur mission. <strong>Nous ne vendons jamais vos données.</strong>
            </p>
            <div className='space-y-4'>
              {sousTraitants.map(st => (
                <div key={st.nom} className='rounded-2xl bg-slate-50 p-4 text-sm'>
                  <div className='mb-1 flex flex-wrap items-center gap-2'>
                    <span className='font-bold text-slate-900'>{st.nom}</span>
                    <span className='rounded-full bg-white px-2 py-0.5 text-xs text-slate-500 border border-slate-200'>
                      {st.role}
                    </span>
                    <span className='rounded-full bg-white px-2 py-0.5 text-xs text-slate-500 border border-slate-200'>
                      {st.lieu}
                    </span>
                  </div>
                  <p className='text-xs text-slate-600'>{st.detail}</p>
                  <p className='mt-1 text-xs text-slate-400'>{st.garantie}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Droits des utilisateurs */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <div className='mb-6 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50'>
                <Lock className='h-5 w-5 text-emerald-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>8. Vos droits</h2>
            </div>
            <p className='mb-4 text-sm text-slate-600'>
              Conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés, vous disposez des
              droits suivants :
            </p>
            <div className='space-y-3'>
              {droitsUtilisateurs.map(item => (
                <div key={item.droit} className='rounded-2xl bg-slate-50 p-4'>
                  <p className='mb-1 font-bold text-slate-900'>{item.droit}</p>
                  <p className='text-xs text-slate-500'>{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Comment exercer */}
            <div className='mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-4'>
              <p className='mb-2 font-bold text-blue-900'>Comment exercer vos droits</p>
              <p className='text-sm text-blue-800'>
                Envoyez votre demande par email à{' '}
                <EmailLink user='dpo' domain='boulesconnect.fr' className='font-semibold underline' /> en indiquant
                votre adresse email de compte et la nature de votre demande. Une pièce d'identité pourra vous être
                demandée en cas de doute raisonnable sur votre identité (Art. 12.6 RGPD). Nous répondrons dans un délai
                maximum de <strong>30 jours</strong> (Art. 12.3 RGPD), prolongeable de 2 mois pour les demandes
                complexes.
              </p>
              <p className='mt-2 text-xs text-blue-700'>
                En cas de réclamation non résolue, vous pouvez saisir la CNIL : 3 Place de Fontenoy, TSA 80715, 75334
                Paris Cedex 07 — ou directement en ligne via{' '}
                <a
                  href='https://www.cnil.fr/fr/plaintes'
                  className='underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  cnil.fr/fr/plaintes
                </a>
                .
              </p>
            </div>
          </div>

          {/* Sécurité */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>9. Sécurité des données</h2>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                MADSAAS SAS met en œuvre des mesures techniques et organisationnelles appropriées (Art. 32 RGPD) pour
                protéger vos données contre la perte, l'altération, la divulgation non autorisée ou l'accès illicite :
              </p>
              <ul className='ml-4 space-y-2'>
                {[
                  'Chiffrement en transit (HTTPS / TLS 1.3)',
                  'Hachage des mots de passe (bcrypt)',
                  'Authentification par tokens JWT à durée de vie courte (15 min) + rotation des refresh tokens',
                  'Révocation centralisée des tokens en cas de déconnexion',
                  "Contrôle d'accès par rôles (RBAC) avec principe du moindre privilège",
                  'Hébergement en France (OVH Cloud, certifié ISO 27001 et ISO 27701)',
                  'Sauvegardes régulières et chiffrées',
                  'Journalisation des accès et actions sensibles (traçabilité)',
                  'Procédure documentée de notification de violation (Art. 33/34 RGPD) : CNIL sous 72h, utilisateurs sans délai en cas de risque élevé',
                ].map(item => (
                  <li key={item} className='flex items-start gap-2'>
                    <span className='mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-600' />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Cookies */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>10. Cookies</h2>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                La Plateforme utilise principalement des cookies <strong>strictement nécessaires</strong> à son
                fonctionnement (session, sécurité CSRF), exempts de consentement en application de l'article 82 de la
                loi Informatique et Libertés. Aucun cookie publicitaire ni tracker tiers n'est déposé sans votre
                consentement préalable.
              </p>
              <p>
                Pour en savoir plus, consultez notre{' '}
                <Link to={ROUTES.COOKIES} className='text-blue-600 hover:underline'>
                  Politique de Cookies
                </Link>
                .
              </p>
            </div>
          </div>

          {/* Transferts hors UE */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>11. Transferts hors Union européenne</h2>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                Vos données sont stockées et traitées dans l'Union européenne (OVH Cloud, France). Le seul transfert
                hors UE concerne <strong>Stripe, Inc.</strong> pour une partie du traitement des paiements. Ce transfert
                est encadré par :
              </p>
              <ul className='ml-4 space-y-2'>
                <li className='flex items-start gap-2'>
                  <span className='mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-600' />
                  les Clauses Contractuelles Types adoptées par la Commission européenne (décision 2021/914),
                </li>
                <li className='flex items-start gap-2'>
                  <span className='mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-600' />
                  la certification de Stripe, Inc. au <strong>EU-US Data Privacy Framework</strong>.
                </li>
              </ul>
            </div>
          </div>

          {/* DPO & Contact */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <div className='mb-6 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50'>
                <Mail className='h-5 w-5 text-blue-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>12. Contact protection des données</h2>
            </div>
            <div className='rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm'>
              <p className='font-bold text-blue-900'>MADSAAS SAS — Référent protection des données</p>
              <p className='mt-1 text-blue-800'>
                Email : <EmailLink user='dpo' domain='boulesconnect.fr' className='underline' />
              </p>
              <p className='text-blue-800'>05110 Lardier-et-Valença, France</p>
              <p className='mt-2 text-xs text-blue-700'>
                Délai de réponse : 30 jours maximum (Art. 12.3 RGPD), prolongeable de 2 mois pour les demandes complexes
                (vous en serez informé).
              </p>
            </div>
          </div>

          {/* Modification */}
          <div className='rounded-3xl border border-slate-100 bg-white p-8 shadow-sm'>
            <h2 className='mb-4 text-xl font-extrabold text-slate-900'>13. Modifications de la politique</h2>
            <div className='space-y-3 text-sm leading-relaxed text-slate-600'>
              <p>
                MADSAAS SAS se réserve le droit de modifier la présente politique à tout moment. Toute modification
                substantielle sera notifiée aux Utilisateurs par email et/ou par notification sur la Plateforme au moins
                15 jours avant son entrée en vigueur. La date de dernière mise à jour est indiquée en haut de ce
                document.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
